import { BaseRepository } from './BaseRepository';

/**
 * Knowledge Article entity type
 */
export interface KnowledgeArticle {
  id: string;
  company_id: string;
  category: string;
  title: string;
  content: string;
  tags: string[];
  view_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Repository for managing knowledge base articles
 * Provides CRUD operations for agent knowledge library
 */
export class KnowledgeArticleRepository extends BaseRepository<KnowledgeArticle> {
  constructor() {
    super({ tableName: 'knowledge_articles', primaryKey: 'id' });
  }

  /**
   * Find all articles for a specific company
   */
  async findByCompany(companyId: string): Promise<KnowledgeArticle[]> {
    return this.findAll({
      filters: { company_id: companyId },
      orderBy: { column: 'category', ascending: true }
    });
  }

  /**
   * Find articles by category
   */
  async findByCategory(
    companyId: string, 
    category: string
  ): Promise<KnowledgeArticle[]> {
    return this.findAll({
      filters: { company_id: companyId, category },
      orderBy: { column: 'title', ascending: true }
    });
  }

  /**
   * Search articles by title or content
   */
  async search(
    companyId: string, 
    searchTerm: string
  ): Promise<KnowledgeArticle[]> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('company_id', companyId)
      .or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`)
      .order('view_count', { ascending: false });

    if (error) {
      throw new Error(`Failed to search articles: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get most viewed articles
   */
  async getMostViewed(
    companyId: string, 
    limit: number = 10
  ): Promise<KnowledgeArticle[]> {
    return this.findAll({
      filters: { company_id: companyId },
      orderBy: { column: 'view_count', ascending: false },
      limit
    });
  }

  /**
   * Increment view count for an article
   */
  async incrementViews(id: string): Promise<KnowledgeArticle> {
    const article = await this.findById(id);
    if (!article) {
      throw new Error('Article not found');
    }

    return this.update(id, { 
      view_count: article.view_count + 1 
    });
  }

  /**
   * Get all unique categories for a company
   */
  async getCategories(companyId: string): Promise<string[]> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('category')
      .eq('company_id', companyId)
      .order('category', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch categories: ${error.message}`);
    }

    // Get unique categories
    const categories = [...new Set(data.map(item => item.category))];
    return categories;
  }

  /**
   * Find articles by tag
   */
  async findByTag(
    companyId: string, 
    tag: string
  ): Promise<KnowledgeArticle[]> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('company_id', companyId)
      .contains('tags', [tag])
      .order('view_count', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch articles by tag: ${error.message}`);
    }

    return data || [];
  }
}

// Export singleton instance
export const knowledgeArticleRepository = new KnowledgeArticleRepository();
